import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import LocationCapture from '../../components/LocationCapture';
import SupermarketLayer from '../../components/SupermarketLayer';

// Fix default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const DEFAULT_CENTER = [-37.8136, 144.9631]; // Melbourne, Australia
const DEFAULT_ZOOM = 12; // ~25km view

const createMarkerIcon = (imageUrl, type, name) => {
  if (imageUrl && imageUrl.startsWith('/uploads/')) {
    return L.icon({
      iconUrl: imageUrl,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -40],
      className: 'rounded-full border-2 border-white shadow-lg'
    });
  }
  
  const isProducer = type === 'producer';
  const color = isProducer ? '#22c55e' : '#3b82f6';
  const emoji = isProducer ? '🚜' : '🏪';
  
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; font-size: 16px; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);">${emoji}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

// Component that adds/updates markers on the map
function MarkerLayer({ vendors, searchResults, searchMode, userLocation }) {
  const map = useMap();
  const markerLayerRef = useRef(null);

  useEffect(() => {
    if (!map) return;

    // Remove old layer
    if (markerLayerRef.current) {
      map.removeLayer(markerLayerRef.current);
    }

    // Create new layer
    const markerLayer = L.layerGroup().addTo(map);
    markerLayerRef.current = markerLayer;

    // Add user location marker
    if (userLocation && userLocation.coordinates) {
      const userMarker = L.marker([userLocation.coordinates[1], userLocation.coordinates[0]], {
        icon: L.divIcon({
          className: 'current-location-marker',
          html: '<div style="background-color: #3b82f6; border-radius: 50%; width: 16px; height: 16px; border: 3px solid white; box-shadow: 0 0 0 4px rgba(59,130,246,0.3);"></div>',
          iconSize: [16, 16],
          iconAnchor: [8, 8]
        })
      }).addTo(markerLayer);
      userMarker.bindPopup('📍 Your Location');
    }

    // Determine vendors to show
    let vendorsToShow = [];
    if (searchMode === 'product') {
      const vendorIds = new Set(searchResults.map(r => r.vendorId));
      vendorsToShow = vendors.filter(v => vendorIds.has(v._id));
    } else {
      vendorsToShow = vendors;
    }

    // Add vendor markers
    vendorsToShow.forEach((vendor) => {
      const pos = getVendorLocation(vendor);
      if (!pos) return;
      
      const vendorName = getVendorName(vendor);
      const vendorSlug = vendor.slug || vendor._id;
      const vendorImage = vendor.logoUrl || vendor.storefront?.logoUrl || vendor.storefrontV2?.logoUrl || null;
      const vendorType = vendor._type || 'producer';
      const isProducerType = vendorType === 'producer';
      
      let productImage = null;
      let productName = null;
      if (searchMode === 'product') {
        const result = searchResults.find(r => r.vendorId === vendor._id);
        if (result) {
          productImage = result.productImage;
          productName = result.productName;
        }
      }
      
      const imageToUse = productImage || vendorImage;
      const markerIcon = createMarkerIcon(imageToUse, vendorType, productName || vendorName);
      const marker = L.marker(pos, { icon: markerIcon }).addTo(markerLayer);
      
      let popupContent = '';
      if (searchMode === 'product' && productName) {
        popupContent = `
          <div class="max-w-[200px]">
            <div class="flex items-center gap-2">
              ${productImage ? `<img src="${productImage}" alt="${productName}" class="w-10 h-10 rounded-full object-cover" />` : '<span class="text-2xl">📦</span>'}
              <div>
                <div class="font-semibold text-sm">${productName}</div>
                <div class="text-xs text-gray-600">${vendorName}</div>
              </div>
            </div>
            <div class="mt-2">
              <a href="/${isProducerType ? 'producer' : 'hub'}/${vendorSlug}" class="text-sm text-blue-600 hover:underline">View Storefront →</a>
            </div>
          </div>
        `;
      } else {
        popupContent = `
          <div class="max-w-[200px]">
            <div class="flex items-center gap-2">
              ${vendorImage ? `<img src="${vendorImage}" alt="${vendorName}" class="w-10 h-10 rounded-full object-cover" />` : `<span class="text-2xl">${isProducerType ? '🚜' : '🏪'}</span>`}
              <div>
                <a href="/${isProducerType ? 'producer' : 'hub'}/${vendorSlug}" class="font-semibold text-green-600 hover:underline">${vendorName}</a>
                ${vendor.tagline ? `<p class="text-xs text-gray-600 mt-1">${vendor.tagline}</p>` : ''}
              </div>
            </div>
            <div class="mt-2">
              <a href="/${isProducerType ? 'producer' : 'hub'}/${vendorSlug}" class="text-sm text-blue-600 hover:underline">View Storefront →</a>
            </div>
          </div>
        `;
      }
      marker.bindPopup(popupContent);
    });

    return () => {
      if (markerLayerRef.current) {
        map.removeLayer(markerLayerRef.current);
      }
    };
  }, [map, vendors, searchResults, searchMode, userLocation]);

  return null;
}

// Component to control map center - ONLY sets view when location changes
function MapCenterController({ center }) {
  const map = useMap();
  const lastCenterRef = useRef(null);

  useEffect(() => {
    if (!center) return;
    
    const centerKey = `${center[0]},${center[1]}`;
    if (lastCenterRef.current !== centerKey) {
      lastCenterRef.current = centerKey;
      map.setView(center, DEFAULT_ZOOM, { animate: true });
    }
  }, [center, map]);

  return null;
}

function getVendorType(vendor) {
  if (vendor.hubSettings) return 'hub';
  if (vendor.farmName) return 'producer';
  return 'producer';
}

function getVendorName(vendor) {
  const type = getVendorType(vendor);
  if (type === 'hub') {
    return vendor.hubSettings?.hubName || vendor.name || 'Hub';
  }
  return vendor.farmName || vendor.name || 'Producer';
}

function getVendorLocation(vendor) {
  if (vendor.location && vendor.location.coordinates) {
    return [vendor.location.coordinates[1], vendor.location.coordinates[0]];
  }
  return null;
}

function ProducersPage() {
  const { user } = useAuth();
  const [producers, setProducers] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [filteredProducers, setFilteredProducers] = useState([]);
  const [filteredHubs, setFilteredHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [userLocation, setUserLocation] = useState(null);
  const [showMap, setShowMap] = useState(true);
  const [maxDistance, setMaxDistance] = useState(25);
  const [cartoApiKey, setCartoApiKey] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchMode, setSearchMode] = useState('vendor');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [allProductNames, setAllProductNames] = useState([]);
  const searchInputRef = useRef(null);
  const [locationFound, setLocationFound] = useState(false);
  const [showColes, setShowColes] = useState(false);
  const [showWoolworths, setShowWoolworths] = useState(false);
  const [showAldi, setShowAldi] = useState(false);
  const [showIGA, setShowIGA] = useState(false);

  // Get Carto API key from server
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch('/api/config/carto-key');
        if (res.ok) {
          const data = await res.json();
          setCartoApiKey(data.key);
        }
      } catch (error) {
        console.error('Failed to fetch Carto key:', error);
      }
    };
    fetchConfig();
  }, []);

  const handleLocationFound = (location) => {
    setUserLocation({
      coordinates: [location.lon, location.lat],
      address: location.address
    });
    setLocationFound(true);
  };

  // Initial fetch on component mount
  useEffect(() => {
    fetchVendors();
    fetchProductNames();
  }, []);

  // Refetch when location changes
  const previousLocationRef = useRef(null);
  useEffect(() => {
    if (userLocation) {
      const currentKey = `${userLocation.coordinates[0]},${userLocation.coordinates[1]}`;
      const previousKey = previousLocationRef.current;
      previousLocationRef.current = currentKey;
      
      if (previousKey && currentKey !== previousKey) {
        fetchVendors();
        fetchProductNames();
      }
    }
  }, [userLocation]);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const producerRes = await fetch('/api/users/producers', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (producerRes.ok) {
        const producerData = await producerRes.json();
        const producersWithLocation = producerData.filter(p => 
          p.location && p.location.coordinates && 
          p.location.coordinates[0] !== 0 && p.location.coordinates[1] !== 0
        );
        setProducers(producersWithLocation);
        setFilteredProducers(producersWithLocation);
      }

      const hubRes = await fetch('/api/users/hubs', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (hubRes.ok) {
        const hubData = await hubRes.json();
        const hubsWithLocation = hubData.filter(h => 
          h.location && h.location.coordinates && 
          h.location.coordinates[0] !== 0 && h.location.coordinates[1] !== 0
        );
        setHubs(hubsWithLocation);
        setFilteredHubs(hubsWithLocation);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductNames = async () => {
    try {
      const res = await fetch('/api/products/names', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAllProductNames(data);
      }
    } catch (error) {
      console.error('Error fetching product names:', error);
    }
  };

  const handleProductSearch = async (searchTerm) => {
    const termToSearch = searchTerm || productSearch;
    if (!termToSearch.trim() || !userLocation) return;
    
    setIsSearching(true);
    setSearchMode('product');
    try {
      const res = await fetch(`/api/products/search?q=${encodeURIComponent(termToSearch.trim())}&lat=${userLocation.coordinates[1]}&lon=${userLocation.coordinates[0]}&radius=${maxDistance}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data);
      }
    } catch (error) {
      console.error('Error searching products:', error);
    } finally {
      setIsSearching(false);
      setShowSuggestions(false);
    }
  };

  const clearSearch = () => {
    setProductSearch('');
    setSearchResults([]);
    setSearchMode('vendor');
    setSuggestions([]);
    setShowSuggestions(false);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setProductSearch(value);
    
    if (value.length >= 2) {
      const matches = allProductNames.filter(name => 
        name.toLowerCase().includes(value.toLowerCase())
      );
      setSuggestions(matches.slice(0, 10));
      setShowSuggestions(matches.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (suggestion) => {
    setProductSearch(suggestion);
    setShowSuggestions(false);
    handleProductSearch(suggestion);
  };

  useEffect(() => {
    if (!userLocation) return;
    
    let filteredProds = producers;
    let filteredHubz = hubs;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filteredProds = filteredProds.filter(p => 
        (p.farmName || p.name || '').toLowerCase().includes(term) ||
        (p.tagline || '').toLowerCase().includes(term)
      );
      filteredHubz = filteredHubz.filter(h => 
        (h.hubSettings?.hubName || h.name || '').toLowerCase().includes(term) ||
        (h.tagline || '').toLowerCase().includes(term)
      );
    }

    if (filterType === 'producers') {
      filteredHubz = [];
    } else if (filterType === 'hubs') {
      filteredProds = [];
    }

    if (userLocation) {
      const maxDist = maxDistance;
      filteredProds = filteredProds.filter(p => {
        const pos = getVendorLocation(p);
        if (!pos) return false;
        const dist = getDistance(userLocation.coordinates[1], userLocation.coordinates[0], pos[0], pos[1]);
        return dist <= maxDist;
      });
      filteredHubz = filteredHubz.filter(h => {
        const pos = getVendorLocation(h);
        if (!pos) return false;
        const dist = getDistance(userLocation.coordinates[1], userLocation.coordinates[0], pos[0], pos[1]);
        return dist <= maxDist;
      });
    }

    setFilteredProducers(filteredProds);
    setFilteredHubs(filteredHubz);
  }, [searchTerm, filterType, producers, hubs, maxDistance, userLocation]);

  const getDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const allVendors = [...filteredProducers, ...filteredHubs].map(vendor => ({
    ...vendor,
    _type: getVendorType(vendor)
  }));

  const mapCenter = userLocation ? [userLocation.coordinates[1], userLocation.coordinates[0]] : DEFAULT_CENTER;
  const selectedBrands = [];
  if (showColes) selectedBrands.push('coles');
  if (showWoolworths) selectedBrands.push('woolworths');
  if (showAldi) selectedBrands.push('aldi');
  if (showIGA) selectedBrands.push('iga');

  const cartoTileUrl = cartoApiKey 
    ? `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=${cartoApiKey}`
    : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading producers and hubs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <h1 className="text-3xl font-bold mb-4">🌱 Find Local Food</h1>
      
      <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
        <LocationCapture onLocationFound={handleLocationFound} />
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Left Sidebar - Controls and Results */}
        <div className="lg:w-80 lg:flex-shrink-0 space-y-4">
          {/* Product Search */}
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <div className="relative">
              <div className="flex gap-1">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={productSearch}
                  onChange={handleSearchInputChange}
                  onKeyDown={(e) => e.key === 'Enter' && handleProductSearch(productSearch)}
                  placeholder="Search products..."
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                {searchMode === 'product' && (
                  <button
                    onClick={clearSearch}
                    className="px-2 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition text-sm"
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
                <button
                  onClick={() => handleProductSearch(productSearch)}
                  disabled={!productSearch.trim() || isSearching || !locationFound}
                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition text-sm"
                >
                  {isSearching ? '⏳' : '🔎'}
                </button>
              </div>
              
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {suggestions.map((suggestion, idx) => (
                    <div
                      key={idx}
                      className="px-3 py-1.5 hover:bg-gray-100 cursor-pointer border-b last:border-b-0 text-sm"
                      onClick={() => selectSuggestion(suggestion)}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {searchResults.length > 0 && (
              <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                <p className="text-xs font-medium text-blue-700">📦 Found {searchResults.length} suppliers</p>
              </div>
            )}
          </div>

          {/* Filter Controls */}
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setFilterType('all')}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition ${
                  filterType === 'all' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilterType('producers')}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition ${
                  filterType === 'producers' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                🚜 Producers
              </button>
              <button
                onClick={() => setFilterType('hubs')}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition ${
                  filterType === 'hubs' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                🏪 Hubs
              </button>
            </div>
          </div>
          {/* Supermarket layer toggles */}
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">🛒 Show supermarkets</h3>
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={showColes}
                  onChange={(e) => setShowColes(e.target.checked)}
                  className="rounded"
                />
                <span style={{ color: '#e01a22' }} className="font-medium">Coles</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={showWoolworths}
                  onChange={(e) => setShowWoolworths(e.target.checked)}
                  className="rounded"
                />
                <span style={{ color: '#178841' }} className="font-medium">Woolworths</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={showAldi}
                  onChange={(e) => setShowAldi(e.target.checked)}
                  className="rounded"
                />
                <span style={{ color: '#00005a' }} className="font-medium">ALDI</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={showIGA}
                  onChange={(e) => setShowIGA(e.target.checked)}
                  className="rounded"
                />
                <span style={{ color: '#7b1a24' }} className="font-medium">IGA</span>
              </label>
            </div>
          </div>

          {/* Results List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex-1 max-h-[300px] lg:max-h-[400px] overflow-y-auto">
            <div className="p-3 bg-gray-50 border-b border-gray-200 sticky top-0">
              <p className="text-xs font-medium text-gray-600">
                {searchMode === 'product' ? searchResults.length : allVendors.length} {searchMode === 'product' ? 'products' : 'vendors'} found
              </p>
            </div>
            <div className="divide-y divide-gray-100">
              {!locationFound ? (
                <div className="p-4 text-center text-gray-400 text-sm">Set location to see results</div>
              ) : searchMode === 'product' ? (
                searchResults.length === 0 ? (
                  <div className="p-4 text-center text-gray-400 text-sm">No products found</div>
                ) : (
                  searchResults.map((result, idx) => {
                    const vendor = allVendors.find(v => v._id === result.vendorId);
                    if (!vendor) return null;
                    const pos = getVendorLocation(vendor);
                    const distance = userLocation && userLocation.coordinates && pos ? getDistance(
                      userLocation.coordinates[1], userLocation.coordinates[0],
                      pos[0], pos[1]
                    ) : null;
                    return (
                      <Link
                        key={idx}
                        to={`/${result.vendorType === 'hub' ? 'hub' : 'producer'}/${result.vendorSlug}`}
                        className="block p-3 hover:bg-gray-50 transition"
                      >
                        <div className="flex items-center gap-2">
                          {result.productImage ? (
                            <img src={result.productImage} alt={result.productName} className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <span className="text-lg">📦</span>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{result.productName}</div>
                            <div className="text-xs text-gray-500 truncate">{result.vendorName}</div>
                          </div>
                          <div className="text-xs text-gray-400">{distance ? distance.toFixed(1) : '?'} km</div>
                        </div>
                      </Link>
                    );
                  })
                )
              ) : (
                allVendors.length === 0 ? (
                  <div className="p-4 text-center text-gray-400 text-sm">No vendors found</div>
                ) : (
                  allVendors.map((vendor) => {
                    const pos = getVendorLocation(vendor);
                    const distance = userLocation && userLocation.coordinates && pos ? getDistance(
                      userLocation.coordinates[1], userLocation.coordinates[0],
                      pos[0], pos[1]
                    ) : null;
                    const vendorName = getVendorName(vendor);
                    const vendorSlug = vendor.slug || vendor._id;
                    const vendorType = vendor._type || 'producer';
                    const isProducerType = vendorType === 'producer';
                    const vendorImage = vendor.logoUrl || vendor.storefront?.logoUrl || vendor.storefrontV2?.logoUrl || null;

                    return (
                      <Link
                        key={vendor._id}
                        to={`/${isProducerType ? 'producer' : 'hub'}/${vendorSlug}`}
                        className="block p-3 hover:bg-gray-50 transition"
                      >
                        <div className="flex items-center gap-2">
                          {vendorImage ? (
                            <img src={vendorImage} alt={vendorName} className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <span className="text-lg">{isProducerType ? '🚜' : '🏪'}</span>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{vendorName}</div>
                            {vendor.tagline && <div className="text-xs text-gray-500 truncate">{vendor.tagline}</div>}
                          </div>
                          <div className="text-xs text-gray-400">{distance ? distance.toFixed(1) : '?'} km</div>
                        </div>
                      </Link>
                    );
                  })
                )
              )}
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="flex-1" style={{ height: '600px' }}>
          <div className="rounded-lg overflow-hidden shadow-lg bg-gray-100" style={{ height: '100%' }}>
            <MapContainer
              center={DEFAULT_CENTER}
              zoom={DEFAULT_ZOOM}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
              zoomControl={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; CartoDB'
                url={cartoTileUrl}
                subdomains='abcd'
                maxZoom={19}
              />
              
              <MapCenterController center={mapCenter} />
              
              <MarkerLayer 
                vendors={allVendors}
                searchResults={searchResults}
                searchMode={searchMode}
                userLocation={userLocation}
              />
              <SupermarketLayer brands={selectedBrands} />
            </MapContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProducersPage;
